import { inject, ref } from "vue";
import { initErrorReporter } from "Util/errorReporter";
import { timeout } from "Util/util";
import { getXhrMethods } from "_Main/app/xhr";
import { getLayoutMethods } from "_Main/app/layout";
import { getControllerMethods } from "_Main/app/controller";
import { KEYS, NAMESPACE } from "Components/page/i18n/_app-base.i18n";
import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NS } from "I18n/common.i18n";
import { LAYOUTS_SYMBOL, VUE_I18N_SYMBOL, PC_RES_SYMBOL } from "./injectionSymbols";

export function createPcApp(
    vueRootApp,
    {
        ProgramId,
        ActionUrls,
        DownloadDocumentUrl,
        UploadFileUrl,
        ErrorReporterUrl,
        PrintStyling,
        DateCultureMessageInfo
    },
    { getXhrHeaders, onInvalidSessionError, transformActionUrls, setUnsavedChanges }
) {
    function getAppLayout() {
        return vueRootApp.runWithContext(() => {
            return inject(LAYOUTS_SYMBOL).app;
        });
    }
    const i18n = vueRootApp.runWithContext(() => {
        return inject(VUE_I18N_SYMBOL).global;
    });
    const res = vueRootApp.runWithContext(() => {
        return inject(PC_RES_SYMBOL);
    });

    const defaultDialogTitle = res({ namespace: NAMESPACE, key: KEYS.defaultDialogTitle });
    const cancelButtonText = res({ namespace: COMMON_NS, key: COMMON_KEYS.cancelText });
    const okButtonText = res({ namespace: COMMON_NS, key: COMMON_KEYS.okText });

    const layoutHelper = getLayoutMethods({ getAppLayout, defaultDialogTitle, cancelButtonText, okButtonText });
    const xhrHelper = getXhrMethods({
        onInvalidSessionError,
        showErrorDialog: layoutHelper.showErrorDialog,
        setUnsavedChanges
    });
    const controllerHelper = getControllerMethods({
        transformActionUrls,
        sendXhr: xhrHelper.sendXhr,
        getXhrHeaders,
        UploadFileUrl,
        ProgramId
    });
    const connectedController = controllerHelper.getConnectedController(ActionUrls);

    async function waitForLongOperation({ operationId, pollIntervalSeconds, progress, deleteWhenComplete, programId }) {
        pollIntervalSeconds = pollIntervalSeconds > 0 ? pollIntervalSeconds : 2;

        let running = true;
        do {
            await timeout(pollIntervalSeconds * 1000);
            const status = await connectedController.GetLongOperationStatus({
                programId: programId ? programId : ProgramId,
                operationId,
                deleteWhenComplete: deleteWhenComplete || null
            });
            if (status.Data.IsRunning) {
                if (progress) {
                    progress(status);
                }
            } else {
                running = false;
                return status;
            }
        } while (running);
    }

    initErrorReporter(controllerHelper.createActionMethod(ErrorReporterUrl), vueRootApp);

    const loading = ref(false);
    const waiting = ref(false);
    const waitMessage = ref(null);

    function blockUi(message) {
        waitMessage.value = message;
        waiting.value = true;
    }
    function unBlockUi() {
        waitMessage.value = null;
        waiting.value = false;
    }

    return {
        ...connectedController,
        ...layoutHelper,
        ...controllerHelper,
        ...xhrHelper,
        waitForLongOperation,
        blockUi,
        unBlockUi,
        timeout,
        ProgramId,
        DownloadDocumentUrl,
        PrintStyling,
        DateCultureMessageInfo,
        loading,
        waiting,
        waitMessage,
        i18n,
        res,
        setUnsavedChanges,
        getXhrHeaders
    };
}
